// src/components/WaterPanel.jsx (s101) — renders the Pass A water chemistry
// panel on a city page. Shows only what an operator document actually says,
// names that document, and says plainly what the operator does not publish.
// Renders nothing until a city has at least two captured fields.
import React from 'react';
import { Link } from 'react-router-dom';
import { FIELDS, fieldDisplay, fieldFlag } from '../data/waterPanel.js';
import { hardnessRow } from '../data/water.js';

export default function WaterPanel({ city, T, fontDisplay, fontBody, fontMono, headingId }) {
  const p = city?.waterPanel || {};
  const hr = hardnessRow(city);
  const keys = Object.keys(FIELDS).filter((k) => k !== 'publishedBy');
  const captured = keys.filter((k) => p[k]?.s === 'captured');
  if (!hr && captured.length < 2) return null;
  // s111: lower-case the labels for mid-sentence use without breaking 'pH'
  // (the old toLowerCase() printed "ph" on ~68 pages).
  const lower = (t) => (/^[a-z][A-Z]/.test(t) ? t : t.charAt(0).toLowerCase() + t.slice(1));
  const absentList = keys.filter((k) => p[k]?.s === 'not-published').map((k) => lower(FIELDS[k].label));
  const absent = absentList.length > 1
    ? `${absentList.slice(0, -1).join(', ')} and ${absentList[absentList.length - 1]}`
    : absentList[0] || '';
  const per = p.publishedBy?.s === 'captured' ? p.publishedBy.unit : null;
  const cell = { padding: '12px 14px 12px 0', verticalAlign: 'top', borderTop: `1px solid ${T.rule}` };

  return (
    <div style={{ marginTop: 40 }}>
      <h3 id={headingId} style={{ fontFamily: fontDisplay, fontSize: 22, fontWeight: 400, letterSpacing: '-0.01em', margin: '0 0 6px', color: T.ink }}>
        {city.cityName} tap water, by the numbers
      </h3>
      {/* s109 3A: the SOURCE line below already names the document and year, so an
          intro only appears when it adds something: per-plant/zone/source reporting. */}
      {per && per !== 'system' ? (
        <p style={{ fontFamily: fontBody, fontSize: 14, lineHeight: 1.6, color: T.inkSoft, margin: '0 0 14px' }}>
          Figures by {per}, as reported.
        </p>
      ) : <div style={{ height: 8 }} />}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: fontBody, fontSize: 15, color: T.ink }}>
          <tbody>
            {hr ? (
              <tr>
                <th scope="row" style={{ ...cell, textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap', width: '28%' }}>Hardness</th>
                <td data-region="water-profile" style={cell}>
                  {hr.value}
                  <div style={{ fontSize: 13.5, lineHeight: 1.55, color: T.inkSoft, marginTop: 4 }}>{hr.note}</div>
                </td>
              </tr>
            ) : null}
            {captured.map((k) => (
              <tr key={k}>
                <th scope="row" style={{ ...cell, textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap', width: '28%' }}>{FIELDS[k].label}</th>
                <td style={cell}>
                  {fieldDisplay(k, p[k])}
                  {/* s111: a short flag linking to the methodology page, not a full
                      sentence repeated across dozens of city pages. */}
                  {fieldFlag(k, p[k]) ? (
                    <div style={{ fontSize: 13.5, lineHeight: 1.55, marginTop: 4 }}>
                      <Link to={`/methodology#${fieldFlag(k, p[k]).anchor}`} style={{ color: T.inkSoft, textDecoration: 'none', borderBottom: `1px dotted ${T.inkSoft}` }}>
                        {fieldFlag(k, p[k]).flag}
                      </Link>
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {absent && (
        <p style={{ fontFamily: fontBody, fontSize: 13.5, lineHeight: 1.6, color: T.inkSoft, margin: '12px 0 0' }}>
          Not in the report: {absent}.
        </p>
      )}
      <p style={{ fontFamily: fontMono, fontSize: 11, lineHeight: 1.6, letterSpacing: '0.02em', color: T.inkSoft, margin: '10px 0 0' }}>
        {p.source ? <>SOURCE · {p.source} · </> : null}<Link to="/methodology#water-table" style={{ color: 'inherit' }}>How we source these numbers</Link>
      </p>
    </div>
  );
}
